// SPDX-FileCopyrightText: Copyright 2024 Dash0 Inc.

"use client";

import React, { ReactNode } from "react";

import SearchedTextHighlighterWithoutAutoExpansion from "@/components/shared/search/search-text-highlighter-without-auto-expansion";
import LazyCodeBlock from "@/components/shared/content/code/LazyCodeBlock";
import { Tag } from "@/components/ui/tag";
import { ImagePreview } from "@/hub/components/image-preview";
import { CheckRuleMetadata } from "@/hub/types";
import { resolveDocumentationUrl } from "@/hub/utils/resolve-documentation-url";

import { localeCompareIgnoreCase } from "@/lib/utils/string";

type CheckRuleCardProps = {
	rule: CheckRuleMetadata;
	body: unknown;
	mdxBody: ReactNode;
	parentIntegrationId: string;
};

export function CheckRuleCard({ rule, body, mdxBody, parentIntegrationId }: CheckRuleCardProps) {
	const { failed, degraded, expression } = readBody(body);
	const previewImage = rule.previews.find((p) => p.type === "image");
	const previewImageSrc = previewImage
		? `/hub/check-rule/preview-image?checkRuleId=${encodeURIComponent(rule.id)}&previewName=${encodeURIComponent(previewImage.src)}`
		: undefined;
	const docsLink = resolveDocumentationUrl(rule.documentationUrl, parentIntegrationId);
	const hasThresholds = failed != null || degraded != null;
	const hasFooterLinks = !!rule.attribution || docsLink.kind !== "hidden";

	return (
		<div
			className="flex flex-col rounded-md p-4 bg-[#1E1E1E] transition-colors gap-4"
			data-testid="hub.check-rule"
			id={`check-rule-${rule.id}`}
		>
			<div className="flex flex-col gap-0.5">
				<h3 className="text-lg font-semibold leading-normal mt-0 mb-2 transition-colors text-[#F3F3F3]">
					<SearchedTextHighlighterWithoutAutoExpansion text={rule.label} />
				</h3>
				<p className="text-base transition-colors">
					<SearchedTextHighlighterWithoutAutoExpansion text={rule.description} />
				</p>
			</div>

			{rule.tags.length > 0 && (
				<div>
					{rule.tags
						.slice()
						.sort(localeCompareIgnoreCase)
						.map((t) => (
							<Tag
								key={t}
								className="inline-flex text-grey-550 items-center rounded-tag grow-0 font-mono text-sm leading-4 px-1 h-4 pointer-events-none mr-1"
							>
								[<SearchedTextHighlighterWithoutAutoExpansion text={t} />]
							</Tag>
						))}
				</div>
			)}

			{previewImage && previewImageSrc && (
				<a href={previewImageSrc} target="_blank" className="cursor-zoom-in">
					<ImagePreview imagePreview={previewImage} src={previewImageSrc} maxWidth={600} maxHeight={200} />
				</a>
			)}

			{mdxBody && (
				<div className="ymdxContent prose-dark [&_p]:text-grey-400 [&_p>strong]:text-white [&_li]:text-[#bdbdbd] prose prose-xs">
					{mdxBody}
				</div>
			)}

			{expression && (
				<LazyCodeBlock
					code={{
						code: expression,
						filename: "",
						language: "promql",
						highlightedLines: [],
					}}
					actions={[]}
					maxHeight={240}
					fullSize
					className="my-0!"
				/>
			)}

			{hasThresholds && (
				<div className="flex flex-wrap items-center gap-2">
					<span className="text-xs uppercase tracking-wide text-grey-550 mr-1">
						Thresholds
					</span>
					{failed != null && <ThresholdPill severity="critical" value={failed} />}
					{degraded != null && <ThresholdPill severity="warning" value={degraded} />}
				</div>
			)}

			{hasFooterLinks && (
				<div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-grey-550 pt-2 border-t border-grey-800">
					{rule.attribution && (
						<a
							href={rule.attribution.link}
							target="_blank"
							rel="noreferrer"
							className="hover:text-intense underline-offset-2 hover:underline"
						>
							Powered by {rule.attribution.name} ↗
						</a>
					)}
					{docsLink.kind !== "hidden" && (
						<a
							href={docsLink.href}
							{...(docsLink.kind === "external"
								? { target: "_blank", rel: "noreferrer" }
								: {})}
							className="hover:text-intense underline-offset-2 hover:underline"
						>
							{docsLink.kind === "external" ? "Setup guide ↗" : "Setup guide"}
						</a>
					)}
				</div>
			)}
		</div>
	);
}

function readBody(body: unknown): {
	failed?: number;
	degraded?: number;
	expression?: string;
} {
	if (!body || typeof body !== "object") {
		return {};
	}
	const expressionValue = (body as { expression?: unknown }).expression;
	const thresholdsValue = (body as { thresholds?: unknown }).thresholds;
	const thresholds =
		thresholdsValue && typeof thresholdsValue === "object"
			? (thresholdsValue as { failed?: unknown; degraded?: unknown })
			: undefined;
	return {
		expression: typeof expressionValue === "string" ? expressionValue : undefined,
		failed: thresholds && typeof thresholds.failed === "number" ? thresholds.failed : undefined,
		degraded:
			thresholds && typeof thresholds.degraded === "number" ? thresholds.degraded : undefined,
	};
}

type Severity = "critical" | "warning";

const severityClasses: Record<Severity, { pill: string; dot: string; label: string }> = {
	critical: {
		pill: "border-red-500/30 bg-red-500/10 text-red-300",
		dot: "bg-red-400",
		label: "Critical",
	},
	warning: {
		pill: "border-amber-500/30 bg-amber-500/10 text-amber-300",
		dot: "bg-amber-400",
		label: "Warning",
	},
};

function ThresholdPill({ severity, value }: { severity: Severity; value: number }) {
	const c = severityClasses[severity];
	return (
		<span
			className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs ${c.pill}`}
		>
			<span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
			{c.label} above {value}
		</span>
	);
}
